import { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  Box,
  Icon,
  Badge,
  Alert,
  AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

import type { Discrepancy, DriverReportsDecision } from 'api/quote-marketplace';

import { useQuoteLock } from '../../hooks';

interface DriverGroup {
  driverId: number;
  driverName: string;
  discrepancies: Discrepancy[];
  allViewed: boolean;
}

interface DiscrepancyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyChanges: () => void;
  onSkipChanges: () => void;
  onViewDriver: (driverId: number, discrepancyIndices: number[]) => void;
  discrepancies: Discrepancy[];
  viewedIndices: number[];
  isApplying?: boolean;
  isSkipping?: boolean;
  decision?: DriverReportsDecision;
  isReviewMode?: boolean;
}

const DiscrepancyModal = ({
  isOpen,
  onClose,
  onApplyChanges,
  onSkipChanges,
  onViewDriver,
  discrepancies,
  viewedIndices,
  isApplying = false,
  isSkipping = false,
  decision = null,
  isReviewMode = false,
}: DiscrepancyModalProps) => {
  const discrepancyColor = useColorModeValue('red.600', 'red.300');
  const viewedColor = useColorModeValue('gray.500', 'gray.400');
  const viewLinkColor = useColorModeValue('blue.600', 'blue.300');

  const { isLocked: isReadOnly } = useQuoteLock();
  const isLoading = isApplying || isSkipping;
  const canChangeDecision =
    !isReadOnly && (decision === 'skipped' || decision === null);
  const isApplied = decision === 'applied';

  const groupedByDriver = useMemo((): DriverGroup[] => {
    const groups = new Map<number, DriverGroup>();

    for (const d of discrepancies) {
      const existing = groups.get(d.driver_id);
      if (existing) {
        existing.discrepancies.push(d);
      } else {
        groups.set(d.driver_id, {
          driverId: d.driver_id,
          driverName: d.driver_name || `Driver ${d.driver_id}`,
          discrepancies: [d],
          allViewed: false,
        });
      }
    }

    for (const group of groups.values()) {
      group.allViewed = group.discrepancies.every((d) =>
        viewedIndices.includes(d.index),
      );
    }

    return Array.from(groups.values());
  }, [discrepancies, viewedIndices]);

  const formatDiscrepancyType = (d: Discrepancy): string => {
    switch (d.type) {
      case 'new_conviction':
        return `New conviction (${d.source.toUpperCase()})`;
      case 'new_claim':
        return `New claim (${d.source.toUpperCase()})`;
      case 'new_lapse':
        return `New lapse (${d.source.toUpperCase()})`;
      case 'suspension_found':
        return `Suspension found (${d.source.toUpperCase()})`;
      case 'accident_found':
        return `Accident found (${d.source.toUpperCase()})`;
      case 'at_fault_mismatch':
        return 'At-fault percentage mismatch';
      default:
        return d.type.replace(/_/g, ' ');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      closeOnOverlayClick={isReviewMode}
      closeOnEsc={isReviewMode}
    >
      <ModalOverlay />
      <ModalContent borderRadius="lg" p={2}>
        {isReviewMode && <ModalCloseButton />}
        <ModalBody pt={6} pb={4}>
          {decision && (
            <Flex mb={4} align="center" gap={2}>
              <Text fontSize="sm" fontWeight="500">
                Decision:
              </Text>
              <Badge
                colorScheme={isApplied ? 'green' : 'orange'}
                fontSize="sm"
                px={2}
                py={1}
              >
                {isApplied ? 'Changes Applied' : 'Skipped'}
              </Badge>
            </Flex>
          )}

          <Text fontSize="lg" fontWeight="bold" mb={4}>
            {decision
              ? 'Discrepancies found between the quote and the reports:'
              : 'The following discrepancies have been found between the quote and the reports:'}
          </Text>

          <Box>
            {groupedByDriver.map((group) => (
              <Box
                key={group.driverId}
                py={3}
                borderBottomWidth="1px"
                borderColor="gray.100"
                _last={{ borderBottomWidth: 0 }}
              >
                <Flex justify="space-between" align="center" mb={2}>
                  <Flex align="center">
                    {group.allViewed && (
                      <Icon
                        as={CheckIcon}
                        color="green.500"
                        mr={2}
                        boxSize={3}
                      />
                    )}
                    <Text
                      fontWeight="600"
                      color={group.allViewed ? viewedColor : 'inherit'}
                    >
                      {group.driverName}
                    </Text>
                  </Flex>
                  <Button
                    size="sm"
                    variant="link"
                    color={viewLinkColor}
                    onClick={() =>
                      onViewDriver(
                        group.driverId,
                        group.discrepancies.map((d) => d.index),
                      )
                    }
                    flexShrink={0}
                  >
                    {group.allViewed ? 'Viewed' : 'View'}
                  </Button>
                </Flex>
                <Box pl={6}>
                  {group.discrepancies.map((d) => (
                    <Text key={d.index} fontSize="sm" color={discrepancyColor}>
                      • {formatDiscrepancyType(d)}
                    </Text>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {isApplied && isReviewMode && (
            <Alert status="info" mt={4} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Changes have been applied. This decision cannot be changed.
              </Text>
            </Alert>
          )}
        </ModalBody>

        <ModalFooter display="flex" flexDirection="column">
          {canChangeDecision && (
            <>
              <Button
                colorScheme="yellow"
                mb="2"
                onClick={onApplyChanges}
                isLoading={isApplying}
                isDisabled={isLoading}
                width="full"
              >
                Quote With Changes
              </Button>
              {!decision && (
                <Button
                  variant="brand"
                  onClick={onSkipChanges}
                  isLoading={isSkipping}
                  isDisabled={isLoading}
                  width="full"
                >
                  Continue Without Changes
                </Button>
              )}
            </>
          )}
          {isReviewMode && (isApplied || isReadOnly) && (
            <Button variant="outline" onClick={onClose} width="full">
              Close
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DiscrepancyModal;
